import React from "react";
import NameWarningsAlert from "../components/NameWarningsAlert";
import { Alert, AlertTitle, Box, Button, Container, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { Form, useParams } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { LocalFormSchema, zLocalFormSchema } from "../logic/applicationUpdateSchema";
import { useProfileAssured } from "../bootstrapping/InitRequireProfile";
import StringFormattedControlledDatePicker from "../components/StringFormattedControlledDatePicker";
import { asOptions, titleMapping } from "../utils/option-data";
import EnumBackedAutocomplete from "../components/EnumBackedAutocomplete";
import OptionalTextFieldArray from "../components/OptionalTextFieldArray";
import HiddenInputForClearFields from "../components/HiddenInputForClearFields";
import { TestingInfoBlock, TestingInfoBlockFormErrors } from "../components/TestingInfo";
import { applicationRefinement } from "../logic/dbsRules";
import { ensureUppercase, trimTextFields } from "../utils/forms";
import { useRedirectToGDPRConsentIfRequired } from "./ApplicationGDPRConsent";
import FormButtons from "../components/FormButtons";
import TestingData from "../components/TestingData";
import { useActionPath } from "./ApplicationUpdate";
import TestApplicationAlert from "../components/TestApplicationAlert";

export const Element: React.FC = () => {
  const { application_id } = useParams<{ application_id?: string }>()
  if (!application_id) throw new Error('application_id is required')

  const profile = useProfileAssured()
  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })
  const readonly = getEBulkApplicationByID.data?.status !== 'draft'

  useRedirectToGDPRConsentIfRequired(application_id)
  const actionPath = useActionPath('birth')

  // Build the Form
  const form = useForm<LocalFormSchema>({
    resolver: zodResolver(
      zLocalFormSchema
        .superRefine(applicationRefinement('names'))
    ),
    mode: 'onBlur',
    defaultValues: {
      ...getEBulkApplicationByID.data?.data as any, // NOTE: This is a hack, as I don't understand the typescript error!
      Title: getEBulkApplicationByID.data?.data.Title ?? '',
      Forename: getEBulkApplicationByID.data?.data.Forename ?? '',
      Middlenames: getEBulkApplicationByID.data?.data.Middlenames ?? { Middlename: [''] }, // NOTE: We start of with one empty Middlename, which will error
      PresentSurname: getEBulkApplicationByID.data?.data.PresentSurname ?? '',
      AdditionalApplicantDetails: {
        ...getEBulkApplicationByID.data?.data.AdditionalApplicantDetails ?? {},
        BirthSurname: getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthSurname ?? null,
        BirthSurnameUntil: getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthSurnameUntil ?? null,
      },
    },
  })

  const _clearFields = form.watch('_clearFields')

  const middlenames = form.watch('Middlenames.Middlename')

  const otherForenames = useFieldArray({
    control: form.control,
    name: 'AdditionalApplicantDetails.OtherForenames.OtherForename',
  })
  const otherSurnames = useFieldArray({
    control: form.control,
    name: 'AdditionalApplicantDetails.OtherSurnames.OtherSurname',
  })

  // Build the output
  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  // Name sanity flags update as the applicant types (DBS-263); warnings only, saving is never blocked
  const nameFieldsBeingEdited = form.watch()

  return <>
    <Helmet>
      <title>{readonly ? 'View' : 'Update'} Names</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          {readonly ? 'View' : 'Update'} DBS Application
        </Typography>
        <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />
        <Typography component="h2" variant="h5" marginBottom={3}>
          Names
        </Typography>
        <Typography variant="body1" marginBottom={1}>
          We need to know a little more about how you are known.
        </Typography>

        <TestingInfoBlock>
          <Alert severity="info" sx={{ marginTop: 3 }}>
            <AlertTitle>Only shown in Test Mode</AlertTitle>
            Whitespace around all text fields will be auto-trimmed.
          </Alert>
        </TestingInfoBlock>

        <Form action={actionPath.url} method="post" onBlur={() => {
          trimTextFields(form)
          ensureUppercase(form, 'Forename')
          middlenames?.forEach((_, index) => {
            ensureUppercase(form, `Middlenames.Middlename.${index}`)
          })
          ensureUppercase(form, 'PresentSurname')
          ensureUppercase(form, 'AdditionalApplicantDetails.BirthSurname')
          otherForenames.fields.forEach((_, index) => {
            ensureUppercase(form, `AdditionalApplicantDetails.OtherForenames.OtherForename.${index}.Name`)
          })
          otherSurnames.fields.forEach((_, index) => {
            ensureUppercase(form, `AdditionalApplicantDetails.OtherSurnames.OtherSurname.${index}.Name`)
          })
        }}>
          {!readonly && (
            <TestingInfoBlock>
              <TestingData form={form} page="names" />
            </TestingInfoBlock>
          )}
          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Current Name
          </Typography>
          <EnumBackedAutocomplete
            control={form.control}
            label="Title"
            name="Title"
            options={asOptions(titleMapping)}
            disabled={readonly}
          />
          <TextFieldElement {...textFieldElementCommonProps} label="Forename" name="Forename" placeholder={profile.given_name} disabled={readonly} />
          <OptionalTextFieldArray
            control={form.control}
            name="Middlenames"
            arrayName="Middlenames.Middlename"
            labelPlural="Middlenames"
            labelSingular="Middlename"
            max={zLocalFormSchema.shape.Middlenames.unwrap().unwrap().shape.Middlename._def.maxLength?.value}
            convertFormValueToLocalState={value => {
              const parsedValue = zLocalFormSchema.shape.Middlenames.parse(value)
              return parsedValue == null
                ? []
                : parsedValue.Middlename
            }}
            convertLocalStateToFormValue={state => {
              if (state.length === 0) {
                form.setValue('_clearFields', { ..._clearFields, Middlenames: 'true' })
                return null
              } else {
                form.setValue('_clearFields', { ..._clearFields, Middlenames: 'false' })
                return { Middlename: state }
              }
            }}
            getFieldError={(error, index) => {
              if (error == null) return undefined
              if ('Middlename' in error && Array.isArray(error.Middlename)) {
                return error.Middlename[index]
              } else {
                return undefined
              }
            }}
            disabled={readonly}
          />
          <TextFieldElement {...textFieldElementCommonProps} label="Present Surname" name="PresentSurname" placeholder={profile.family_name} disabled={readonly} />
          <NameWarningsAlert names={nameFieldsBeingEdited} sx={{ marginTop: 1 }} />

          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Name at Birth
          </Typography>
          <TextFieldElement
            {...textFieldElementCommonProps}
            label="Birth Surname"
            name="AdditionalApplicantDetails.BirthSurname"
            disabled={readonly}
          />
          <StringFormattedControlledDatePicker
            control={form.control}
            sx={{ marginTop: 3, width: '100%' }}
            label="Birth Surname Used Until (year only)"
            name="AdditionalApplicantDetails.BirthSurnameUntil"
            views={['year']}
            format="yyyy"
            disabled={readonly}
          />

          <Alert severity="info" sx={{ marginTop: 3 }}>
            <AlertTitle>Any other names?</AlertTitle>
            If you have <strong>ever</strong> been known by <strong>any</strong> other names, please provide them below.
            <br /><br />
            Your application is likely to be <strong>withdrawn without refund</strong> if you do not declare all names you have been known by.
          </Alert>

          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Other Forenames
          </Typography>
          <Table aria-label="simple table">
            <TableBody>
              {otherForenames.fields.map((eachOtherForename, index) => (
                <React.Fragment key={eachOtherForename.id}>
                  <TableRow>
                    <TableCell colSpan={3} style={{ paddingLeft: 0, borderBottom: "none" }}>
                      <TextFieldElement
                        control={form.control}
                        fullWidth
                        label="Name"
                        name={`AdditionalApplicantDetails.OtherForenames.OtherForename.${index}.Name`}
                        disabled={readonly}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingLeft: 0 }}>
                      <StringFormattedControlledDatePicker
                        control={form.control}
                        label="From"
                        name={`AdditionalApplicantDetails.OtherForenames.OtherForename.${index}.UsedFrom`}
                        views={['year']}
                        format="yyyy"
                        disabled={readonly}
                      />
                    </TableCell>
                    <TableCell style={{ paddingRight: 0 }}>
                      <StringFormattedControlledDatePicker
                        control={form.control}
                        label="To"
                        name={`AdditionalApplicantDetails.OtherForenames.OtherForename.${index}.UsedTo`}
                        views={['year']}
                        format="yyyy"
                        disabled={readonly}
                      />
                    </TableCell>
                    {!readonly && (
                      <TableCell style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <Button sx={{ marginLeft: 1 }} onClick={() => otherForenames.remove(index)}>Remove</Button>
                      </TableCell>
                    )}
                  </TableRow>
                </React.Fragment>
              ))}
              {!readonly && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Button onClick={() => {
                      otherForenames.append({
                        // Defaults used to ensure all the components are controlled.
                        // All fields 
                        Name: '',
                        UsedFrom: null,
                        UsedTo: null,
                      } as any)
                    }}>Add Forename</Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Other Surnames
          </Typography>
          <Table aria-label="simple table">
            <TableBody>
              {otherSurnames.fields.map((eachOtherSurname, index) => (
                <React.Fragment key={eachOtherSurname.id}>
                  <TableRow>
                    <TableCell colSpan={3} style={{ paddingLeft: 0, borderBottom: "none" }}>
                      <TextFieldElement
                        control={form.control}
                        fullWidth
                        label="Name"
                        name={`AdditionalApplicantDetails.OtherSurnames.OtherSurname.${index}.Name`}
                        disabled={readonly}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingLeft: 0 }}>
                      <StringFormattedControlledDatePicker
                        control={form.control}
                        label="From"
                        name={`AdditionalApplicantDetails.OtherSurnames.OtherSurname.${index}.UsedFrom`}
                        views={['year']}
                        format="yyyy"
                        disabled={readonly}
                      />
                    </TableCell>
                    <TableCell style={{ paddingRight: 0 }}>
                      <StringFormattedControlledDatePicker
                        control={form.control}
                        label="To"
                        name={`AdditionalApplicantDetails.OtherSurnames.OtherSurname.${index}.UsedTo`}
                        views={['year']}
                        format="yyyy"
                        disabled={readonly}
                      />
                    </TableCell>
                    {!readonly && (
                      <TableCell style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <Button sx={{ marginLeft: 1 }} onClick={() => otherSurnames.remove(index)}>Remove</Button>
                      </TableCell>
                    )}
                  </TableRow>
                </React.Fragment>
              ))}
              {!readonly && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Button onClick={() => {
                      otherSurnames.append({
                        // Defaults used to ensure all the components are controlled.
                        // All fields 
                        Name: '',
                        UsedFrom: null,
                        UsedTo: null,
                      } as any)
                    }}>Add Surname</Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <HiddenInputForClearFields _clearFields={_clearFields} />

          <TestingInfoBlockFormErrors form={form} />

          <FormButtons actionPath={actionPath} form={form} readonly={readonly} />
        </Form>
      </Box>
    </Container>
  </>
}

export default Element
